import type { CommandInputFormSpec } from '@/components/commands/CommandInputForm';
import { bulkRunIsPartial, type BulkEditSummary, type BulkRowRef } from './bulkEdit';

/**
 * A command on the selected rows of a list view (docs/15-designspecs/Bulk-Edit-And-Actions.md §4.3, §6.2): run once on
 * the server over every row, each through the command's own endpoint, with one outcome per row — the same outcome a
 * bulk edit has, so the summary, the refused rows and the warned rows are read with bulkEdit.ts.
 */

/** What a generated list view hands the shell for one `showInBulk` command. */
export interface BulkCommandConfig {
  /** Stable key, e.g. `cancel`. */
  key: string;
  /** The command's name, as available-actions identifies it (`Cancel`). */
  command: string;
  /** The command's name as the menu shows it (already translated). */
  label: string;
  /** POST endpoint, e.g. `/api/sales/order/bulk/cancel`. */
  endpoint: string;
  /** The command's declared input, asked once and sent for every row (D6); absent when it declares none. */
  input?: CommandInputFormSpec;
}

/** One command over the selected rows, as each row's own available-actions answered it (§8.1). */
export interface BulkCommandAvailability {
  command: string;
  total: number;
  available: number;
  /** The selected rows that can run it; absent from a server older than the field. */
  availableIds?: string[];
  /** Why the other rows cannot run it, each reason once. */
  reasons: string[];
}

/** The rows a command is run on, and those left out because their own available-actions said they cannot. */
export interface BulkCommandRun {
  rows: BulkRowRef[];
  leftOut: number;
  reasons: string[];
}

/**
 * Runs a command only on the selected rows that can run it (§8.1): the menu asked them just before. Without an answer
 * naming the rows (the question failed, or the server does not name them) every selected row is sent, and each row's
 * command decides.
 */
export function bulkCommandRun(rows: readonly BulkRowRef[], availability: BulkCommandAvailability | null): BulkCommandRun {
  if (!availability?.availableIds) return { rows: [...rows], leftOut: 0, reasons: [] };
  const able = new Set(availability.availableIds);
  const runnable = rows.filter((row) => able.has(row.id));
  return { rows: runnable, leftOut: rows.length - runnable.length, reasons: availability.reasons };
}

/** How the Actions menu offers a command for the selection: unavailable with the reasons, or on how many rows. */
export function bulkCommandOffer(
  config: BulkCommandConfig,
  availability: readonly BulkCommandAvailability[] | null,
  t: (key: string, options?: Record<string, unknown>) => string,
): { label: string; disabled: boolean; reasons: string[] } {
  const found = availability?.find((a) => a.command === config.command);
  // Not asked yet, or the question failed: offer it — each row's command still decides when it runs.
  if (!found) return { label: config.label, disabled: false, reasons: [] };
  if (found.available === 0) return { label: config.label, disabled: true, reasons: found.reasons };
  return found.available < found.total
    ? { label: t('bulkCommand.availableOf', { command: config.label, available: found.available, total: found.total }), disabled: false, reasons: found.reasons }
    : { label: config.label, disabled: false, reasons: [] };
}

/** The request body (§6.2): the rows with the stamps they were selected with, the input, and acknowledged keys. */
export function bulkCommandRequest(
  rows: readonly BulkRowRef[],
  input: Record<string, unknown>,
  acknowledgedWarnings: readonly string[] = [],
) {
  return {
    selection: { rows: rows.map((r) => ({ id: r.id, concurrencyStamp: r.concurrencyStamp })) },
    input,
    acknowledgedWarnings: [...acknowledgedWarnings],
  };
}

/**
 * The list's snackbar for a finished command: a row refused (listed in the panel) or left warned makes it a warning.
 */
export function bulkCommandOutcome(
  summary: BulkEditSummary,
  command: string,
  t: (key: string, options?: Record<string, unknown>) => string,
): { severity: 'success' | 'warning'; message: string } {
  return bulkRunIsPartial(summary)
    ? { severity: 'warning', message: t('bulkCommand.partlyApplied', { command, applied: summary.applied, total: summary.total }) }
    : { severity: 'success', message: t('bulkCommand.applied', { command, count: summary.applied }) };
}
